import {DateTimeFormat} from '@hconnect/common/components/dateTime/DateTimeFormat'
import {OverdueLabel} from '@hconnect/common/components/eventProperties/OverdueLabel'
import {TableSize} from '@hconnect/common/components/eventsList/types'
import {StatusWithCancellation} from '@hconnect/common/types'
import {PaginationOptions, selectableTableRowSx} from '@hconnect/uikit'
import {DataTable, MarkdownText} from '@hconnect/uikit/src/lib2'
import type {Column} from '@hconnect/uikit/src/lib2'
import {getPlantName, UpmPlantsContextValue, useUpmPlantsContext} from '@hconnect/upm'
import {Tooltip, Typography} from '@mui/material'
import {EventStatusLabel} from '@widgets/common-shift-event'
import {TFunction} from 'i18next'
import moment from 'moment-timezone'
import {useTranslation} from 'react-i18next'

import {FEATURE_NAMESPACE} from '../consts'
import {HProduceTask, SortOrder, TasksTableKey, TasksTableSortOptions} from '../types'

const generateColumnsMap = (t: TFunction, timezone: string, upmPlants: UpmPlantsContextValue) => ({
  XS: [generateTitleColumn({t})],
  S: [
    generateTitleColumn({t, width: '90%'}),
    generateStatusColumn({t, width: '10%', type: 'small'})
  ],
  M: [
    generateTitleColumn({t, width: '90%'}),
    generateStatusColumn({t, width: '10%', type: 'small'})
  ],
  L: [
    generateTitleColumn({t, width: '25%', includeOverdueLabel: false}),
    generateAssigneeColumn({t, width: '15%'}),
    generateDueDateColumn({t, width: '10%', timezone}),
    generateCreatedAtColumn({t, width: '15%', timezone}),
    generateMaintenanceNotificationColumn({t, width: '10%'}),
    generateStatusColumn({t, width: '10%', type: 'small'}),
    generatePlantColumn({t, width: '10%', upmPlants})
  ],
  XL: [
    generateTitleColumn({t, width: '25%', includeOverdueLabel: false}),
    generateAssigneeColumn({t, width: '15%'}),
    generateDueDateColumn({t, width: '15%', timezone}),
    generateCreatedAtColumn({t, width: '10%', timezone}),
    generateMaintenanceNotificationColumn({t, width: '10%'}),
    generateStatusColumn({t, width: '10%', type: 'regular'}),
    generatePlantColumn({t, width: '15%', upmPlants})
  ]
})

type Props = {
  data: HProduceTask[]
  isLoading: boolean
  paginationOptions?: PaginationOptions
  sortOptions?: TasksTableSortOptions
  selectedItemId?: string
  showDescriptions?: boolean
  onRowClick: (clickedItem: HProduceTask) => void
  size?: TableSize
  areFiltersApplied: boolean
  timezone: string
}

export const TasksTable = ({
  data,
  isLoading,
  paginationOptions,
  sortOptions,
  selectedItemId,
  onRowClick,
  showDescriptions,
  size = 'L',
  timezone,
  areFiltersApplied
}: Props) => {
  const {t} = useTranslation(FEATURE_NAMESPACE)
  const upmPlants = useUpmPlantsContext()
  const columnsMap = generateColumnsMap(t, timezone, upmPlants)
  const columns = columnsMap[size]
  const emptyMessageTranslationKey = areFiltersApplied
    ? 'tasksTable.noDataForSelectedFilters'
    : 'label.noData'

  return (
    <DataTable<HProduceTask>
      columns={columns}
      data={data ?? []}
      loading={isLoading}
      paginationOptions={paginationOptions}
      {...(sortOptions && getSortProps(sortOptions))}
      emptyMessage={(!isLoading && t(`${emptyMessageTranslationKey}`)) ?? ''}
      data-test-id="tasks-table"
      onRowClick={(_, clickedItem) => onRowClick(clickedItem)}
      rowSx={(item) => selectableTableRowSx(selectedItemId === item.id)}
      rowFooterTemplate={showDescriptions ? ({rowData}) => renderDescription(rowData) : undefined}
    />
  )
}

const getSortProps = (sortOptions: TasksTableSortOptions) => ({
  sortedBy: sortOptions.sortBy,
  sortDirection: getSortDirection(sortOptions.sortOrder),
  onSort: (_: unknown, columnKey: TasksTableKey) => {
    const newSortOrder = getNextSortOrder(sortOptions, columnKey)
    sortOptions.onSortChange?.(columnKey, newSortOrder)
  }
})

const getSortDirection = (sortOrder?: SortOrder): 'asc' | 'desc' | undefined => {
  if (!sortOrder) {
    return undefined
  }
  return sortOrder === SortOrder.ASCENDING ? 'asc' : 'desc'
}

const getNextSortOrder = (
  sortOptions: TasksTableSortOptions,
  columnKey: TasksTableKey
): SortOrder => {
  return sortOptions.sortBy === columnKey && sortOptions.sortOrder === SortOrder.ASCENDING
    ? SortOrder.DESCENDING
    : SortOrder.ASCENDING
}

const renderDescription = ({id, description}: HProduceTask) => (
  <Typography
    component="div"
    sx={{
      display: '-webkit-box',
      WebkitLineClamp: 3,
      WebkitBoxOrient: 'vertical',
      overflow: 'hidden'
    }}
    data-test-id={`tasks-table-${id}-description`}
    color="textSecondary"
    variant="body2"
  >
    {description && <MarkdownText>{description}</MarkdownText>}
  </Typography>
)

type GenerateColumnArgs = {
  t: TFunction
  width?: string
}

export const generateTitleColumn = <T extends HProduceTask>({
  t,
  width,
  includeOverdueLabel = true
}: GenerateColumnArgs & {
  includeOverdueLabel?: boolean
}): Column<T> => ({
  key: TasksTableKey.TITLE,
  label: t('tasksTable.label.title'),
  width: width ? width : undefined,
  sortable: true,
  customTemplate: (item: T) =>
    includeOverdueLabel ? (
      <>
        {item.title}{' '}
        <OverdueLabel
          status={item.status as StatusWithCancellation}
          dueDate={moment(item.dueDate)}
          plantNow={moment()}
          translationFunc={t}
        />
      </>
    ) : (
      item.title
    )
})

export const generateStatusColumn = <T extends HProduceTask>({
  t,
  width,
  type
}: GenerateColumnArgs & {type?: 'small' | 'regular'}): Column<T> => ({
  headProps: type === 'small' ? {style: {paddingLeft: '4px'}} : {},
  key: TasksTableKey.STATUS,
  width: width ? width : '0px',
  label: t('tasksTable.label.status'),
  sortable: true,
  customTemplate: (item: T) => <EventStatusLabel status={item.status} iconOnly={type === 'small'} />
})

export const generateDueDateColumn = <T extends HProduceTask>({
  t,
  width,
  timezone
}: GenerateColumnArgs & {timezone: string}): Column<T> => ({
  key: TasksTableKey.DUE_DATE,
  label: t('tasksTable.label.dueDate'),
  width,
  sortable: true,
  customTemplate: (item: T) => (
    <>
      <Tooltip title={t('tasksTable.dueDateTooltip')} placement="bottom">
        <span>
          <DateTimeFormat date={item.dueDate} timezone={timezone} />
        </span>
      </Tooltip>{' '}
      <OverdueLabel
        status={item.status as StatusWithCancellation}
        dueDate={moment(item.dueDate)}
        plantNow={moment()}
        translationFunc={t}
      />
    </>
  )
})

export const generateAssigneeColumn = <T extends HProduceTask>({
  t,
  width
}: GenerateColumnArgs): Column<T> => ({
  key: TasksTableKey.ASSIGNEE,
  label: t('tasksTable.label.assignee'),
  sortable: true,
  width,
  customTemplate: ({assignees}) =>
    assignees?.length ? assignees.map(({name}) => name).join(', ') : '-'
})

export const generateCreatedAtColumn = <T extends HProduceTask>({
  t,
  width,
  timezone
}: GenerateColumnArgs & {timezone: string}): Column<T> => ({
  key: TasksTableKey.CREATED,
  label: t('tasksTable.label.createdAt'),
  width,
  sortable: true,
  customTemplate: (item: T) => <DateTimeFormat date={item.created} timezone={timezone} />
})

export const generatePlantColumn = <T extends HProduceTask>({
  t,
  width,
  upmPlants
}: GenerateColumnArgs & {upmPlants: UpmPlantsContextValue}): Column<T> => ({
  key: TasksTableKey.PLANT,
  label: t('tasksTable.label.plant'),
  width,
  sortable: true,
  customTemplate: ({plantId}) => getPlantName(upmPlants, plantId)
})

export const generateMaintenanceNotificationColumn = <T extends HProduceTask>({
  t,
  width
}: GenerateColumnArgs): Column<T> => ({
  key: TasksTableKey.MAINTENANCE_NOTIFICATION,
  label: t('tasksTable.label.maintenanceNotification'),
  width,
  sortable: true,
  customTemplate: ({sapMaintenanceNotification}) =>
    sapMaintenanceNotification?.sapMaintenanceNotificationId || '-'
})
